import type { Account, Person, Transaction } from '@fd/schemas';

/**
 * A transfer or settlement payment between the household's people under the person's name, not the account holder's
 * name the bank gives ("K Stankowska" → "Karolina", "M Krysiak" → "Marcin"; the owner, 02.10.2026). The other side
 * is the owner of the counterpart account when that account is in the app and has one owner; a settlement payment
 * without one is with the household's one other person. Anything else keeps the bank's name (null).
 */
export function memberName(
  transaction: Pick<Transaction, 'kind' | 'accountId' | 'counterpartAccountId'>,
  accounts: readonly Pick<Account, 'id' | 'owners'>[],
  persons: readonly Pick<Person, 'id' | 'displayName' | 'archivedAt'>[],
): string | null {
  if (transaction.kind !== 'TRANSFER' && transaction.kind !== 'SETTLEMENT_PAYMENT') return null;
  const nameOf = (id: string | undefined) => (id ? (persons.find((p) => p.id === id)?.displayName ?? null) : null);
  const counterpart = transaction.counterpartAccountId ? accounts.find((a) => a.id === transaction.counterpartAccountId) : undefined;
  if (counterpart) return counterpart.owners.length === 1 ? nameOf(counterpart.owners[0]) : null;
  if (transaction.kind !== 'SETTLEMENT_PAYMENT') return null;
  const own = accounts.find((a) => a.id === transaction.accountId)?.owners ?? [];
  const others = persons.filter((p) => !p.archivedAt && !own.includes(p.id));
  return others.length === 1 ? others[0]!.displayName : null;
}
